<script lang="ts">
    import DealBadge from '@tile/components/product-image/DealBadge.svelte';
    import type { ImageAspectRatio } from '@tile/components/product-image/types.ts';
    import Rating from '@tile/components/rating/Rating.svelte';
    import PriceDispatcher from '@tilelist/layouts/dispatchers/price-dispatcher/PriceDispatcher.svelte';
    import Card from '@tilelist/layouts/experiments/e3867/card/Card.svelte';
    import type { DefaultLayout } from '@tilelist/layouts/experiments/e3867/default-layout/DefaultLayout.ts';
    import ProductImageLink from '@tilelist/layouts/experiments/e3867/product-image-link/ProductImageLink.svelte';
    import ProductImage from '@tilelist/layouts/experiments/e3867/product-image/ProductImage.svelte';
    import ShortTitle from '@tilelist/layouts/experiments/e3867/short-title/ShortTitle.svelte';
    import SocialProofBadges from '@tilelist/layouts/experiments/e3867/social-proof-badges/SocialProofBadges.svelte';
    import type { TileLayoutProps } from '@tilelist/layouts/shared/TileLayoutProps.ts';

    type Props = TileLayoutProps & {
        layout: DefaultLayout;
        aspectRatio: ImageAspectRatio;
    };

    let {
        layout,
        aspectRatio,
        imageLoadingOptions,
        onRedirectToPdp,
        rule,
        element = $bindable(null),
    }: Props = $props();

    const { variation, title, link } = $derived(layout);
</script>

<Card
    data-jarvis="reptile-tile-container"
    data-variation-id={variation.variationId}
    bind:element
    disableOverlayEffect
    size="50"
>
    <!-- NOTE: This is used in the Card component and is passed through -->
    {#snippet primaryAction()}
        <a
            id={`link-${variation.variationId}`}
            href={link.href}
            data-primary-action
            aria-label={variation.title?.withoutBrand || variation.title?.full || ''}
            onclick={onRedirectToPdp}
        ></a>
    {/snippet}

    <div class="reptile-clean-layout__image">
        <ProductImage {aspectRatio}>
            <ProductImageLink
                image={layout.primaryImage}
                variationId={variation.variationId}
                detailPageLink={variation.detailPageLink}
                onClick={onRedirectToPdp}
                {imageLoadingOptions}
                {aspectRatio}
                shouldDim={layout.shouldDim}
            />
        </ProductImage>
        <!-- DealBadge and SocialProofBadges are self-positioning atoms (own position: absolute),
             rendered as flat siblings here rather than nested inside ProductImage/ProductImageLink,
             so both stay independently clickable/composable (see README). -->
        {#if variation.deal}
            <DealBadge deal={variation.deal} variationId={variation.variationId} {rule} />
        {/if}
        <!-- TODO: Should we break these up? -->
        <SocialProofBadges socialProof={variation.socialProof} />
    </div>

    <ShortTitle {title} brandOnTop={true} />

    <!-- Rating opens its own sheet on click (see Rating.svelte), so it needs
         data-secondary-action to opt back into pointer-events under the card's
         primary-link overlay. -->
    <div data-secondary-action>
        <Rating customerReviews={variation.customerReviews} />
    </div>

    <div>
        <PriceDispatcher
            variationId={variation.variationId}
            saleTags={variation.saleTags}
            price={variation.price}
            dealHighlight={variation.deal?.highlight}
            {rule}
        />
    </div>
</Card>

<style lang="scss">
    .reptile-clean-layout__image {
        position: relative;
    }
</style>
